import { type MouseEvent, useCallback, useId, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { Swiper as SwiperType } from 'swiper';

import Carousel, { useCarousel } from '_components/Carousel';
import { isModifiedClick } from '_utils/GA4/clickTracking';
import { trackBrandTileClick } from '_utils/GA4/trackBrandTilesEvent';
import { useLocale } from '@hooks/useLocale';
import { localizeHref } from '@utils/localizeHref';

import BrandCard from './BrandCard';
import type { BrandTilesData } from './types';
import styles from './BrandTiles.module.scss';

// Two cards fit the narrowest viewport; any beyond that start off-screen on mobile.
const PRIORITY_CARD_COUNT = 2;

interface BrandTilesProps extends BrandTilesData {
  priority?: boolean;
}

interface BrandTilesInnerProps extends Pick<BrandTilesData, 'tiles'> {
  heading: string;
  headingId: string;
  priority: boolean;
}

function BrandTilesInner({
  heading,
  headingId,
  tiles,
  priority,
}: BrandTilesInnerProps) {
  const { t } = useTranslation('common');
  const locale = useLocale();
  const { asPath } = useRouter();
  const interactionLocation = asPath.split('?')[0];
  const {
    state: { isScrollable },
  } = useCarousel();

  const [statusMessage, setStatusMessage] = useState('');

  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      setStatusMessage(
        t('brandTiles.slideStatus', {
          current: swiper.activeIndex + 1,
          total: tiles.length,
        })
      );
    },
    [tiles.length, t]
  );

  const handleTileClick = (
    event: MouseEvent<HTMLAnchorElement>,
    href: string,
    coOpBrand: string | null,
    index: number
  ) => {
    const data = {
      interactionLocation,
      linkUrl: href,
      coOpBrand,
      index,
      heading,
    };

    if (isModifiedClick(event) || event.defaultPrevented) {
      trackBrandTileClick({ data });
      return;
    }

    event.preventDefault();
    trackBrandTileClick({
      data,
      eventCallback: () => {
        window.location.href = href;
      },
    });
  };

  return (
    <>
      <div className={styles.header}>
        <h2 id={headingId} className={styles.heading}>
          {heading}
        </h2>

        {isScrollable && (
          <div className={styles.buttonGroup}>
            <Carousel.NavPrev
              aria-label={t('brandTiles.prev')}
              className={styles.navButton}
              iconSize={24}
            />
            <Carousel.NavNext
              aria-label={t('brandTiles.next')}
              className={styles.navButton}
              iconSize={24}
            />
          </div>
        )}
      </div>

      <div
        className={styles.track}
        role="group"
        aria-roledescription={t('brandTiles.carousel')}
        aria-label={heading}
      >
        <Carousel.Track
          className={styles.swiper}
          wrapperTag="ul"
          onSlideChange={handleSlideChange}
        >
          {tiles.map((tile, index) => {
            const href = localizeHref(tile.url, locale);

            return (
              <Carousel.Slide
                key={`${tile.url}-${tile.logo.url}`}
                tag="li"
                className={styles.slide}
              >
                <BrandCard
                  href={href}
                  logo={tile.logo}
                  image={tile.image}
                  ctaLabel={tile.ctaLabel}
                  priority={priority && index < PRIORITY_CARD_COUNT}
                  onClick={(event) =>
                    handleTileClick(event, href, tile.coOpBrand, index)
                  }
                />
              </Carousel.Slide>
            );
          })}
        </Carousel.Track>
      </div>

      <p className={styles.status} role="status" aria-live="polite">
        {statusMessage}
      </p>
    </>
  );
}

const BrandTiles = ({ heading, tiles, priority = false }: BrandTilesProps) => {
  const headingId = useId();

  if (!heading || !tiles?.length) return null;

  return (
    <section
      className={styles.root}
      aria-labelledby={headingId}
      data-testid="brand-tiles"
    >
      <div className={styles.inner}>
        <Carousel.Provider>
          <BrandTilesInner
            heading={heading}
            headingId={headingId}
            tiles={tiles}
            priority={priority}
          />
        </Carousel.Provider>
      </div>
    </section>
  );
};

export default BrandTiles;
